import { Routes } from '@angular/router';
import { adminGuard } from './guard/admin.guard';
import { authGuard } from './guard/auth.guard';
import { ddrIncludedGuard } from './guard/ddr-included.guard';
import { learnReleaseGuard } from './guard/learn-release.guard';
import { redirectAuthenticatedGuard } from './guard/redirect-authenticated.guard';

export const appRoutes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'chat',
    },
    {
        path: '',
        // The guard sits on `login` and `signup` only, NOT on this shell. Those two
        // are the sign-in surfaces; the other children are not, and a signed-in user
        // has legitimate reasons to open any of them — `passwordReset` and `verify`
        // as email landings, `forgot-password` and `request-access` as forms they
        // fill in themselves. Bouncing any of them to /chat reads to the user as the
        // page being broken. Adding a new child here grants it to signed-in users
        // too; put the guard on the child only if it is itself a sign-in surface.
        loadComponent: () => import('./pages/auth/auth-layout').then(m => m.AuthLayout),
        children: [
            {
                path: 'login',
                canActivate: [redirectAuthenticatedGuard],
                loadComponent: () => import('./pages/auth/login-form/login-form').then(m => m.LoginForm),
                data: { mode: 'login' },
            },
            {
                path: 'signup',
                canActivate: [redirectAuthenticatedGuard],
                loadComponent: () => import('./pages/auth/signup-form/signup-form').then(m => m.SignupForm),
                data: { mode: 'signup' },
            },
            {
                // PFG demo: a rep's code creates a trial offer, then hands off to /signup.
                path: 'join',
                canActivate: [redirectAuthenticatedGuard],
                loadComponent: () => import('./pages/auth/pfg-join/pfg-join').then(m => m.PfgJoin),
                data: { mode: 'join' },
            },
            {
                path: 'verify',
                loadComponent: () => import('./pages/auth/verify-form/verify-form').then(m => m.VerifyForm),
                data: { mode: 'verify' },
            },
            {
                path: 'forgot-password',
                loadComponent: () => import('./pages/auth/forgot-password-form/forgot-password-form').then(m => m.ForgotPasswordForm),
                data: { mode: 'forgotPassword' },
            },
            {
                path: 'passwordReset',
                loadComponent: () => import('./pages/auth/password-reset-form/password-reset-form').then(m => m.PasswordResetForm),
                data: { mode: 'passwordReset' },
            },
            {
                path: 'request-access',
                loadComponent: () => import('./pages/auth/request-access/request-access').then(m => m.RequestAccess),
                data: { mode: 'requestAccess' },
            },
            {
                // Landing for the Unsubscribe link in notification emails
                // (CIQ-1615). Deliberately inside this shell and NOT guarded:
                // the recipient may have no account at all, so an auth gate
                // would make the opt-out unreachable for the people most likely
                // to want it.
                path: 'unsubscribe',
                loadComponent: () => import('./pages/auth/unsubscribe/unsubscribe').then(m => m.Unsubscribe),
                data: { mode: 'unsubscribe' },
            },
        ],
    },
    {
        // Stale-cookie re-auth bounce target. A custom app on its
        // `{app_id}.apps.*` subdomain redirects here when the `ciq_session`
        // cookie has expired; this page refreshes the session (re-minting
        // the cookie) and navigates back to the app. Intentionally NO
        // authGuard - it must run even when the in-memory token looks off.
        path: 'app-reauth',
        loadComponent: () => import('./pages/app-reauth/app-reauth').then(m => m.AppReauth),
    },
    {
        path: 'auth/sso/callback',
        loadComponent: () => import('./pages/auth/sso-callback/sso-callback').then(m => m.SsoCallback),
    },
    {
        // Terminal screen after deleting your only workspace (CIQ-1276). No
        // authGuard and no redirectAuthenticatedGuard: by the time this renders
        // the account is archived and the session has been revoked server-side,
        // so any guard that consults auth state would bounce the user off the
        // one page that explains what just happened.
        path: 'account-closed',
        loadComponent: () => import('./pages/account-closed/account-closed').then(m => m.AccountClosed),
    },
    {
        path: 'onboarding/identity',
        loadComponent: () => import('./pages/onboarding-identity/onboarding-identity').then(m => m.OnboardingIdentity),
    },
    {
        // Intentionally unguarded, like the onboarding routes above: authGuard sends
        // blank-name users *here*, so gating this route would loop (CIQ-1234). The
        // component redirects to /login itself when there is no authenticated user.
        path: 'complete-your-profile',
        loadComponent: () => import('./pages/complete-profile/complete-profile').then(m => m.CompleteProfile),
    },
    {
        path: 'onboarding/corporate',
        loadComponent: () => import('./pages/onboarding-corporate/onboarding-corporate').then(m => m.OnboardingCorporate),
    },
    {
        // The live post-login destination for every workspace-less user:
        // authGuard, redirectAuthenticatedGuard and onboarding-identity all send
        // here. /onboarding/corporate above is reached only when this page has
        // nothing to create a workspace from, or the account matches an
        // existing one by domain.
        path: 'onboarding/setup',
        loadComponent: () => import('./pages/onboarding-setup/onboarding-setup').then(m => m.OnboardingSetup),
    },
    {
        path: 'workspace/accept_invite',
        loadComponent: () => import('./pages/accept-invite/accept-invite').then(m => m.AcceptInvite),
    },
    {
        path: 'accept-invite',
        redirectTo: 'workspace/accept_invite',
        pathMatch: 'full',
    },
    {
        // Consent screen for a user sync source, opened from an app or the studio.
        path: 'app-connect/:appId/:collection',
        canActivate: [authGuard],
        loadComponent: () => import('./pages/app-connect/app-connect').then(m => m.AppConnect),
    },
    {
        // The studio lives in the Apps view now.
        path: 'studio/:appId',
        redirectTo: ({ params }) => `/chat/workspace/apps?app=${encodeURIComponent(params['appId'] ?? '')}`,
    },
    {
        path: 'chat/workspace/:view',
        canActivate: [authGuard],
        loadComponent: () => import('./pages/chat-with-collectiv-iq/chat-with-collectiv-iq').then(m => m.ChatWithCollectivIQ),
    },
    {
        path: 'chat/ddr/room/:roomId',
        canActivate: [authGuard, ddrIncludedGuard],
        loadComponent: () => import('./pages/chat-with-collectiv-iq/chat-with-collectiv-iq').then(m => m.ChatWithCollectivIQ),
    },
    {
        path: 'chat/ddr/review/:personaId',
        canActivate: [authGuard, ddrIncludedGuard],
        loadComponent: () => import('./pages/chat-with-collectiv-iq/chat-with-collectiv-iq').then(m => m.ChatWithCollectivIQ),
    },
    {
        path: 'chat/ddr/dm/:personaId',
        canActivate: [authGuard, ddrIncludedGuard],
        loadComponent: () => import('./pages/chat-with-collectiv-iq/chat-with-collectiv-iq').then(m => m.ChatWithCollectivIQ),
    },
    {
        // Every DDR file link lands here (backend file_chat_link + the API's
        // redirect for legacy /api/ddr/files/... navigations): signed-in fetch
        // with the bearer token, then download or inline preview.
        path: 'files/:id',
        canActivate: [authGuard],
        loadComponent: () => import('./pages/file-open/file-open').then(m => m.FileOpen),
    },
    {
        // DDRs sometimes write the Files page path plus an id; land it on the file.
        path: 'chat/workspace/files/:id',
        redirectTo: 'files/:id',
    },
    {
        path: 'chat',
        canActivate: [authGuard],
        loadComponent: () => import('./pages/chat-with-collectiv-iq/chat-with-collectiv-iq').then(m => m.ChatWithCollectivIQ),
    },
    {
        path: 'learn',
        canActivate: [authGuard, learnReleaseGuard],
        loadComponent: () => import('./pages/collectiv-iq-university/collectiv-iq-university').then(m => m.CollectivIqUniversity),
    },
    {
        path: 'admin',
        canActivate: [authGuard, adminGuard],
        loadComponent: () => import('./pages/admin/admin-layout').then(m => m.AdminLayout),
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'app-audit',
            },
            {
                path: 'app-audit',
                loadComponent: () => import('./pages/admin/app-audit/app-audit').then(m => m.AppAudit),
            },
            {
                path: 'spend',
                loadComponent: () => import('./pages/admin/admin-spend/admin-spend').then(m => m.AdminSpend),
            },
            {
                path: 'spend-events',
                loadComponent: () => import('./pages/admin/admin-spend-events/admin-spend-events').then(m => m.AdminSpendEvents),
            },
            {
                // Legacy URL — spend/events moved to its own top-level segment so
                // the nav's exact path matching stays simple. Preserves query
                // params (drill-down links carried user_id/workspace_id).
                path: 'spend/events',
                redirectTo: 'spend-events',
            },
            {
                // Legacy URL — the Activity page merged into /admin/users
                // (per-user engagement) and /admin/corporate-workspaces
                // (per-workspace aggregates).
                path: 'analytics',
                redirectTo: 'users',
            },
            {
                path: 'jobs',
                loadComponent: () => import('./pages/admin/admin-jobs/admin-jobs').then(m => m.AdminJobs),
            },
            {
                path: 'kpis',
                loadComponent: () => import('./pages/admin/admin-kpis/admin-kpis').then(m => m.AdminKpis),
            },
            {
                path: 'tool-telemetry',
                loadComponent: () => import('./pages/admin/admin-tool-telemetry/admin-tool-telemetry').then(m => m.AdminToolTelemetry),
            },
            {
                path: 'mcp',
                loadComponent: () => import('./pages/admin/admin-mcp/admin-mcp').then(m => m.AdminMcp),
            },
            {
                path: 'corporate-workspaces',
                loadComponent: () =>
                    import('./pages/admin/admin-corporate-workspaces/admin-corporate-workspaces').then(m => m.AdminCorporateWorkspaces),
            },
            {
                path: 'users',
                loadComponent: () => import('./pages/admin/admin-users/admin-users').then(m => m.AdminUsers),
            },
            {
                path: 'learn',
                loadComponent: () => import('./pages/admin/admin-learn/admin-learn').then(m => m.AdminLearn),
            },
            {
                path: 'changelogs',
                loadComponent: () => import('./pages/admin/admin-changelogs/admin-changelogs').then(m => m.AdminChangelogs),
            },
            {
                path: 'ddr-satisfaction',
                loadComponent: () =>
                    import('./pages/admin/admin-ddr-satisfaction/admin-ddr-satisfaction').then(m => m.AdminDdrSatisfaction),
            },
            {
                // Unknown admin sub-route (e.g. a stale bookmark or typo) — bounce
                // back to the admin index rather than falling through to the
                // top-level '**' route, which would dump the user out to /chat.
                // Still nested under admin's authGuard/adminGuard.
                path: '**',
                redirectTo: '',
            },
        ],
    },
    {
        // Legacy deep-link — the approvals surface lives at the workspace view.
        path: 'approvals',
        redirectTo: 'chat/workspace/approvals',
        pathMatch: 'full',
    },
    {
        path: '**',
        redirectTo: 'chat',
    },
];

function extractPaths(routes: Routes, parentPath = ''): string[] {
    const paths: string[] = [];

    for (const route of routes) {
        if (route.path !== undefined && route.path !== '**') {
            const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path;

            paths.push(fullPath || '/');

            if (route.children) {
                paths.push(...extractPaths(route.children, fullPath));
            }
        }
    }

    return paths;
}

const allPaths = extractPaths(appRoutes);

export const PATHS = allPaths.reduce(
    (acc, path) => {
        const key = path.replace(/^\//, '').replace(/\//g, '_').replace(/:/g, '').toUpperCase() || 'ROOT';

        acc[key] = path;

        return acc;
    },
    {} as Record<string, string>
);
